const understandingYourDogProgress: Record<
	UnderstandingYourDogScreenNames,
	number
> = {
	UYD_INTRO: 0,
	Q12: 0,
	Q13: 15,
	Q14: 30,
	Q15: 45,
	Q15_1: 60,
	Q16: 75,
	Q17: 90,
};

export const getNextScreenUnderstandingYourDog = (
	surveyState: NDS.SurveyState,
	formData: NDS.FormData,
	questionSchema: NDS.QuestionSchema,
): NDS.RouteChange => {
	const screenState = surveyState.routeStack[surveyState.routeStack.length - 1];

	switch (screenState.screen as UnderstandingYourDogScreenNames) {
		// Understanding your dog
		case "UYD_INTRO": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				UNDERSTANDING_YOUR_DOG: understandingYourDogProgress.Q12,
			};
			surveyState.routeStack.push({
				...screenState,
				route: "UNDERSTANDING_YOUR_DOG",
				screen: "Q12",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// How would you describe [dogName]?
		case "Q12": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				UNDERSTANDING_YOUR_DOG: understandingYourDogProgress.Q13,
			};
			surveyState.routeStack.push({
				...screenState,
				route: "UNDERSTANDING_YOUR_DOG",
				screen: "Q13",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Which of these can you do with [dog_name]?
		case "Q13": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				UNDERSTANDING_YOUR_DOG: understandingYourDogProgress.Q14,
			};
			surveyState.routeStack.push({
				...screenState,
				route: "UNDERSTANDING_YOUR_DOG",
				screen: "Q14",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// How much time did you spend yesterday doing these activities with [dog_name]?
		case "Q14": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				UNDERSTANDING_YOUR_DOG: understandingYourDogProgress.Q15,
			};
			surveyState.routeStack.push({
				...screenState,
				route: "UNDERSTANDING_YOUR_DOG",
				screen: "Q15",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Have you experienced any of these training or behaviour challenges with [dog_name] in the last 12 months?
		case "Q15": {
			const notSureIndex =
				questionSchema.UNDERSTANDING_YOUR_DOG.Q15.fields[0].options.length - 3;
			const notSureValue =
				questionSchema.UNDERSTANDING_YOUR_DOG.Q15.fields[0].options[
					notSureIndex
				].value;

			const noneOfTheAboveIndex =
				questionSchema.UNDERSTANDING_YOUR_DOG.Q15.fields[0].options.length - 2;

			const noneOfTheAboveValue =
				questionSchema.UNDERSTANDING_YOUR_DOG.Q15.fields[0].options[
					noneOfTheAboveIndex
				].value;

			const selectedBehaviour = formData.nds_q_15_behaviour_1 as string[];

			// If the selected behaviour includes either notSureValue or noneOfTheAboveValue
			if (
				selectedBehaviour?.includes(notSureValue) ||
				selectedBehaviour?.includes(noneOfTheAboveValue)
			) {
				// We can skip to Q16
				formData.nds_q_15_info_seeking_behaviour = undefined;

				surveyState.surveyProgress = {
					...surveyState.surveyProgress,
					UNDERSTANDING_YOUR_DOG: understandingYourDogProgress.Q16,
				};
				surveyState.routeStack.push({
					...screenState,
					route: "UNDERSTANDING_YOUR_DOG",
					screen: "Q16",
				});
				return {
					surveyState,
					formData,
				};
			}
			const otherIndex =
				questionSchema.UNDERSTANDING_YOUR_DOG.Q15.fields[0].options.length - 1;

			const otherValue =
				questionSchema.UNDERSTANDING_YOUR_DOG.Q15.fields[0].options[otherIndex]
					.value;

			// If there is one selected behaviour and it is not other or there is multiple selected behaviours:
			if (
				selectedBehaviour?.length > 1 ||
				(selectedBehaviour?.length === 1 &&
					!selectedBehaviour?.includes(otherValue))
			) {
				// We can go to to Q15_1

				// We don't want to include Other in the random selection
				const selectedBehaviourWithoutOther = selectedBehaviour?.filter(
					(value) => value !== otherValue,
				);
				const randomAnswer = selectedBehaviourWithoutOther?.includes(
					formData.nds_q_15_info_seeking_behaviour as string,
				)
					? formData.nds_q_15_info_seeking_behaviour
					: selectedBehaviour?.[(Math.random() * selectedBehaviour.length) | 0];

				formData.nds_q_15_info_seeking_behaviour = randomAnswer;

				surveyState.surveyProgress = {
					...surveyState.surveyProgress,
					UNDERSTANDING_YOUR_DOG: understandingYourDogProgress.Q15_1,
				};
				surveyState.routeStack.push({
					...screenState,
					route: "UNDERSTANDING_YOUR_DOG",
					screen: "Q15_1",
				});
				return {
					surveyState,
					formData,
				};
			}

			// If there is multiple selected behaviours, we want to ignore Other and randomly select one of the other behaviours

			// Otherwise we always go to Q16
			formData.nds_q_15_info_seeking_behaviour = undefined;

			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				UNDERSTANDING_YOUR_DOG: understandingYourDogProgress.Q16,
			};
			surveyState.routeStack.push({
				...screenState,
				route: "UNDERSTANDING_YOUR_DOG",
				screen: "Q16",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Have you used any of these services to help with [selected_behaviour]?
		case "Q15_1": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				UNDERSTANDING_YOUR_DOG: understandingYourDogProgress.Q16,
			};
			surveyState.routeStack.push({
				...screenState,
				route: "UNDERSTANDING_YOUR_DOG",
				screen: "Q16",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// How much do you agree or disagree with these statements?
		case "Q16": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				UNDERSTANDING_YOUR_DOG: understandingYourDogProgress.Q17,
			};
			surveyState.routeStack.push({
				...screenState,
				route: "UNDERSTANDING_YOUR_DOG",
				screen: "Q17",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// How much do you agree or disagree with these statements?
		case "Q17": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				UNDERSTANDING_YOUR_DOG: 100,
				RELATIONSHIP_WITH_YOUR_DOG: 0,
			};
			surveyState.routeStack.push({
				...screenState,
				route: "RELATIONSHIP_WITH_YOUR_DOG",
				screen: "RYD_INTRO",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
				transition: "UNDERSTANDING_YOUR_DOG",
			};
		}
	}
};
